import type { AuditLog } from '../../models/AuditLog';
import type { AuditActionType } from '../../models/enums';
import { apiClient } from '../api/apiClient';
import { createId, delay, readCollection, seedCollection, writeCollection } from './dataverseClient';
import { auditLogsSeed } from './mockData';

const ENTITY = 'cr_crm_auditlog';

export interface CreateAuditLogInput {
  actionType: AuditActionType;
  entityName: string;
  entityId?: string;
  description: string;
  userId?: string;
  userName?: string;
  metadata?: Record<string, unknown>;
}

export interface AuditLogListParams {
  page?: number;
  pageSize?: number;
  entityName?: string;
  action?: string;
  from?: string;
  to?: string;
}

export interface AuditLogListResult {
  items: AuditLog[];
  totalCount: number;
}

class AuditLogService {
  private ensureSeed(): void {
    seedCollection(ENTITY, auditLogsSeed);
  }

  async list(params?: AuditLogListParams): Promise<AuditLogListResult> {
    if (apiClient.isConfigured()) {
      const qs = new URLSearchParams();
      if (params?.page) qs.set('page', String(params.page));
      if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
      if (params?.entityName) qs.set('entityName', params.entityName);
      if (params?.action) qs.set('action', params.action);
      if (params?.from) qs.set('from', params.from);
      if (params?.to) qs.set('to', params.to);
      const res = await apiClient.get<AuditLogListResult | AuditLog[]>(
        `/api/audit-logs${qs.toString() ? `?${qs.toString()}` : ''}`
      );
      if (Array.isArray(res)) return { items: res, totalCount: res.length };
      return res as AuditLogListResult;
    }

    this.ensureSeed();
    await delay();
    let items = readCollection<AuditLog>(ENTITY).sort((a, b) => b.createdon.localeCompare(a.createdon));
    if (params?.entityName) items = items.filter((l) => l.cr_crm_entityname === params.entityName);
    const pageSize = params?.pageSize ?? 20;
    const page = params?.page ?? 1;
    const start = (page - 1) * pageSize;
    return { items: items.slice(start, start + pageSize), totalCount: items.length };
  }

  /** Fire-and-forget helper used by other services */
  async record(input: CreateAuditLogInput): Promise<AuditLog> {
    if (apiClient.isConfigured()) {
      try {
        return await apiClient.post<AuditLog>('/api/audit-logs', input);
      } catch {
        // Non-blocking: audit failures should not break normal operations
        const fallback: AuditLog = {
          cr_crm_auditlogid: createId('audit'),
          cr_crm_actiontype: input.actionType,
          cr_crm_entityname: input.entityName,
          cr_crm_entityid: input.entityId,
          cr_crm_description: input.description,
          createdon: new Date().toISOString(),
        };
        return fallback;
      }
    }

    this.ensureSeed();
    await delay(80);
    const entry: AuditLog = {
      cr_crm_auditlogid: createId('audit'),
      cr_crm_actiontype: input.actionType,
      cr_crm_entityname: input.entityName,
      cr_crm_entityid: input.entityId,
      cr_crm_description: input.description,
      cr_crm_metadata: input.metadata ? JSON.stringify(input.metadata) : undefined,
      cr_crm_username: input.userName,
      cr_crm_userid: input.userId,
      createdon: new Date().toISOString(),
    };
    writeCollection(ENTITY, [entry, ...readCollection<AuditLog>(ENTITY)]);
    return entry;
  }
}

export const auditLogService = new AuditLogService();
